import { TrendingUp, TrendingDown, AlertTriangle, RefreshCw } from 'lucide-react';
import { useFetchETFPrice } from '@etfSecurityHooks';
import { Button } from '@/components/ui/button';

interface ETFPriceDisplayProps {
  symbol: string;
  exchange?: string;
}

/**
 * Displays real-time ETF price data for a given symbol.
 * Falls back to a warning with a retry button when the API is unavailable.
 */
export default function ETFPriceDisplay({ symbol, exchange }: ETFPriceDisplayProps) {
  const { data, isLoading, error, refetch } = useFetchETFPrice(symbol, exchange);

  if (isLoading) {
    return <span className="text-xs text-muted-foreground">Loading price...</span>;
  }

  if (error || !data) {
    return (
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
          <AlertTriangle className="h-3 w-3" aria-hidden="true" />
          Price unavailable
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs"
          onClick={() => refetch()}
          aria-label={`Retry fetching price for ${symbol}`}
        >
          <RefreshCw className="mr-1 h-3 w-3" aria-hidden="true" />
          Retry
        </Button>
      </div>
    );
  }

  const isUp = data.change >= 0;
  return (
    <div className="flex items-center gap-1 text-xs">
      <span className="font-mono">
        {data.currency ?? '$'}{data.price.toFixed(2)}
      </span>
      <span className={isUp ? 'text-emerald-600' : 'text-red-600'}>
        {isUp ? '+' : ''}{data.changePercent.toFixed(2)}%
      </span>
      {isUp ? (
        <TrendingUp className="h-3 w-3 text-emerald-600" aria-hidden="true" />
      ) : (
        <TrendingDown className="h-3 w-3 text-red-600" aria-hidden="true" />
      )}
    </div>
  );
}
